import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAPI } from './useAPI'
import { listResultsByCustomer } from '@/thunks/results/customer'
import { Result } from '@/out/result'
import { App } from '@/apps'
import { debounce } from '@/util/debounce'
import { useDelayedLoading } from './useDelayedLoading'

type DateRange = {
  from?: Date
  to?: Date
}

export type GlobalSearchFilters = {
  app?: App | null
  status?: 'success' | 'failure' | 'timeout' | null
  dateRange?: DateRange | null
}

type GlobalSearchState = {
  isOpen: boolean
  initialQuery: string
  filters: GlobalSearchFilters
}

let globalSearchState: GlobalSearchState = {
  isOpen: false,
  initialQuery: '',
  filters: {},
}

const listeners = new Set<() => void>()

function notifyListeners() {
  listeners.forEach(listener => listener())
}

export function openGlobalSearch(initialQuery: string = '', filters: GlobalSearchFilters = {}) {
  globalSearchState = {
    isOpen: true,
    initialQuery,
    filters,
  }
  notifyListeners()
}

function closeGlobalSearch() {
  globalSearchState = {
    isOpen: false,
    initialQuery: '',
    filters: {},
  }
  notifyListeners()
}

export function useGlobalSearch() {
  const [searchResults, { timer }] = useAPI(listResultsByCustomer)
  const [results, setResults] = useState<Result[]>([])
  const [filteredResults, setFilteredResults] = useState<Result[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const activeRequestRef = useRef<AbortController | null>(null)
  const [isOpen, setIsOpen] = useState(globalSearchState.isOpen)
  const [searchQuery, setSearchQuery] = useState(globalSearchState.initialQuery)
  const [filters, setFilters] = useState<GlobalSearchFilters>(globalSearchState.filters)

  const applyFilters = useCallback(() => {
    if (!results.length) {
      setFilteredResults([])
      return
    }

    const filtered = results.filter(result => {
      if (filters.app && result.app !== filters.app) {
        return false
      }

      if (filters.status && result.status !== filters.status) {
        return false
      }

      if (filters.dateRange) {
        const resultDate = new Date(result.timestamp)
        if (filters.dateRange.from && resultDate < filters.dateRange.from) {
          return false
        }
        if (filters.dateRange.to) {
          const endOfDay = new Date(filters.dateRange.to)
          endOfDay.setHours(23, 59, 59, 999)
          if (resultDate > endOfDay) {
            return false
          }
        }
      }

      return true
    })

    setFilteredResults(filtered)
  }, [results, filters])

  const performSearch = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setResults([])
        setFilteredResults([])
        return
      }

      // Cancel any in-flight request
      if (activeRequestRef.current) {
        activeRequestRef.current.abort()
      }

      // Create new abort controller for this request
      const abortController = new AbortController()
      activeRequestRef.current = abortController

      setIsLoading(true)
      setError(null)

      try {
        const request = {
          customerRef: query.trim(),
        }

        const [data] = await searchResults(request)

        if (!abortController.signal.aborted) {
          setResults(data || [])
          setFilteredResults(data || [])
        }
      } catch {
        if (!abortController.signal.aborted) {
          setError(new Error('An error occurred during search'))
          setResults([])
          setFilteredResults([])
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false)
          activeRequestRef.current = null
        }
      }
    },
    [searchResults]
  )

  const search = useMemo(() => debounce((query: string) => performSearch(query), 300), [performSearch])

  useEffect(() => {
    return () => {
      if (activeRequestRef.current) {
        activeRequestRef.current.abort()
      }
      search.cancel()
    }
  }, [search])

  // Execute search when query changes
  useEffect(() => {
    if (searchQuery.trim()) {
      search(searchQuery)
    } else {
      setResults([])
      setFilteredResults([])
    }
  }, [searchQuery, search])

  // Apply filters whenever filters or results change
  useEffect(() => {
    applyFilters()
  }, [filters, applyFilters])

  // Sync with global state
  useEffect(() => {
    const syncWithGlobalState = () => {
      setIsOpen(globalSearchState.isOpen)
      setFilters(globalSearchState.filters)
      if (globalSearchState.isOpen && globalSearchState.initialQuery) {
        setSearchQuery(globalSearchState.initialQuery)
      }
    }

    syncWithGlobalState()
    listeners.add(syncWithGlobalState)

    return () => {
      listeners.delete(syncWithGlobalState)
    }
  }, [])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        closeGlobalSearch()
        setSearchQuery('')
        setResults([])
        setFilteredResults([])
      } else {
        openGlobalSearch(searchQuery)
      }
    },
    [searchQuery]
  )

  // Use delayed loading to prevent spinner flickering
  const delayedLoading = useDelayedLoading(isLoading || timer.isLoading)

  return {
    results: filteredResults,
    unfilteredResults: results,
    isLoading: delayedLoading,
    error,
    search,
    isOpen,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    handleOpenChange,
  }
}
